import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar as CalendarIcon, Clock, User, Mail, Phone, MapPin, FileText, Flame, Wrench, Hammer, Sparkles, Check, FileSignature, Wallet } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { format } from 'date-fns';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceOptions = [
  { value: 'welding', label: 'Welding', icon: Flame },
  { value: 'mechanics', label: 'Mechanics', icon: Wrench },
  { value: 'handyman', label: 'Handyman', icon: Hammer },
  { value: 'cleaning', label: 'Cleaning', icon: Sparkles },
];

const timeSlots = [
  '08:00 AM', '09:00 AM', '10:00 AM', '11:00 AM',
  '12:00 PM', '01:00 PM', '02:00 PM', '03:00 PM',
  '04:00 PM', '05:00 PM'
];

export default function Booking() {
  const [formData, setFormData] = useState({
    customer_name: '',
    email: '',
    phone: '',
    service_type: '',
    preferred_date: null,
    preferred_time: '',
    address: '',
    description: '',
    request_contract: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name || !formData.email || !formData.phone || 
        !formData.service_type || !formData.preferred_date || 
        !formData.preferred_time || !formData.address) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = {
        ...formData,
        preferred_date: format(formData.preferred_date, 'yyyy-MM-dd'),
      };
      
      await axios.post(`${API}/bookings`, submitData);
      setIsSuccess(true);
      toast.success('Booking submitted successfully!');
    } catch (error) {
      console.error('Error submitting booking:', error);
      toast.error('Failed to submit booking. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <main className="min-h-screen pt-24 pb-16 px-6 md:px-12 lg:px-24 flex items-center justify-center" data-testid="booking-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass rounded-2xl p-12 text-center max-w-lg"
        >
          <div className="w-20 h-20 rounded-full bg-[#00F0FF]/20 flex items-center justify-center mx-auto mb-6 cyan-glow">
            <Check className="w-10 h-10 text-[#00F0FF]" />
          </div>
          <h2 className="font-heading text-3xl font-bold mb-4">Booking Confirmed!</h2>
          <p className="text-gray-400 mb-8">
            Thank you for choosing OmniBeast Services. We'll review your request and
            contact you shortly with your free estimate. A deposit will be required
            based on your estimate to secure your appointment.
          </p>
          <Button
            onClick={() => {
              setIsSuccess(false);
              setFormData({
                customer_name: '',
                email: '',
                phone: '',
                service_type: '',
                preferred_date: null,
                preferred_time: '',
                address: '',
                description: '',
                request_contract: false,
              });
            }}
            className="btn-primary rounded-none px-8 py-5 font-heading"
            data-testid="book-another-button"
          >
            BOOK ANOTHER SERVICE
          </Button>
        </motion.div>
      </main>
    );
  }

  return (
    <main className="min-h-screen pt-24 pb-16" data-testid="booking-page">
      {/* Hero Section */}
      <section className="px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-[#00F0FF] font-medium tracking-wider uppercase text-sm">
              Schedule Your Service
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black mt-4 mb-6">
              Book <span className="gradient-text">Appointment</span>
            </h1>
            <p className="text-gray-400 max-w-2xl text-lg">
              Fill out the form below and we'll get back to you within 24 hours 
              to confirm your booking.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto">
          <motion.form
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            onSubmit={handleSubmit}
            className="glass rounded-2xl p-8 md:p-12"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-gray-300 flex items-center gap-2">
                  <User className="w-4 h-4 text-[#00F0FF]" />
                  Full Name *
                </Label>
                <Input
                  id="name"
                  data-testid="booking-name-input"
                  placeholder="John Doe"
                  value={formData.customer_name}
                  onChange={(e) => handleChange('customer_name', e.target.value)}
                  className="bg-[#0A0F14] border-[#1E293B] text-white placeholder:text-gray-600 rounded-none focus:border-[#00F0FF]"
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-300 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-[#00F0FF]" />
                  Email Address *
                </Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="booking-email-input"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="bg-[#0A0F14] border-[#1E293B] text-white placeholder:text-gray-600 rounded-none focus:border-[#00F0FF]"
                />
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-gray-300 flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[#00F0FF]" />
                  Phone Number *
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  data-testid="booking-phone-input"
                  placeholder="(555) 123-4567"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="bg-[#0A0F14] border-[#1E293B] text-white placeholder:text-gray-600 rounded-none focus:border-[#00F0FF]"
                />
              </div>

              {/* Service Type */}
              <div className="space-y-2">
                <Label className="text-gray-300 flex items-center gap-2">
                  <Wrench className="w-4 h-4 text-[#00F0FF]" />
                  Service Type *
                </Label>
                <Select
                  value={formData.service_type}
                  onValueChange={(value) => handleChange('service_type', value)}
                >
                  <SelectTrigger 
                    data-testid="booking-service-select"
                    className="bg-[#0A0F14] border-[#1E293B] text-white rounded-none focus:border-[#00F0FF]"
                  >
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0F14] border-[#1E293B]">
                    {serviceOptions.map((service) => (
                      <SelectItem 
                        key={service.value} 
                        value={service.value}
                        className="text-white hover:bg-[#1E293B] focus:bg-[#1E293B]"
                      >
                        <span className="flex items-center gap-2">
                          <service.icon className="w-4 h-4 text-[#00F0FF]" />
                          {service.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date */}
              <div className="space-y-2">
                <Label className="text-gray-300 flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4 text-[#00F0FF]" />
                  Preferred Date *
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      data-testid="booking-date-picker"
                      className={`w-full bg-[#0A0F14] border-[#1E293B] text-left font-normal rounded-none hover:bg-[#1E293B] ${
                        !formData.preferred_date ? 'text-gray-600' : 'text-white'
                      }`}
                    >
                      {formData.preferred_date ? (
                        format(formData.preferred_date, 'PPP')
                      ) : (
                        'Pick a date'
                      )}
                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-[#0A0F14] border-[#1E293B]" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.preferred_date}
                      onSelect={(date) => handleChange('preferred_date', date)}
                      disabled={(date) => date < new Date()}
                      initialFocus
                      className="bg-[#0A0F14]"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Time */}
              <div className="space-y-2">
                <Label className="text-gray-300 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#00F0FF]" />
                  Preferred Time *
                </Label>
                <Select
                  value={formData.preferred_time}
                  onValueChange={(value) => handleChange('preferred_time', value)}
                >
                  <SelectTrigger 
                    data-testid="booking-time-select"
                    className="bg-[#0A0F14] border-[#1E293B] text-white rounded-none focus:border-[#00F0FF]"
                  >
                    <SelectValue placeholder="Select a time" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0F14] border-[#1E293B]">
                    {timeSlots.map((time) => (
                      <SelectItem 
                        key={time} 
                        value={time}
                        className="text-white hover:bg-[#1E293B] focus:bg-[#1E293B]"
                      >
                        {time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Address */}
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="address" className="text-gray-300 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#00F0FF]" />
                  Service Address *
                </Label>
                <Input
                  id="address"
                  data-testid="booking-address-input"
                  placeholder="123 Main St, City, State 12345"
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  className="bg-[#0A0F14] border-[#1E293B] text-white placeholder:text-gray-600 rounded-none focus:border-[#00F0FF]"
                />
              </div>

              {/* Description */}
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="description" className="text-gray-300 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#00F0FF]" />
                  Job Description (Optional)
                </Label>
                <Textarea
                  id="description"
                  data-testid="booking-description-input"
                  placeholder="Please describe the work you need done..."
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  className="bg-[#0A0F14] border-[#1E293B] text-white placeholder:text-gray-600 rounded-none focus:border-[#00F0FF] min-h-[120px]"
                />
              </div>

              {/* Request a Contract */}
              <div className="md:col-span-2">
                <div className="flex items-start gap-4 p-5 rounded-xl border border-[#1E293B] bg-[#0A0F14]/60">
                  <Checkbox
                    id="request_contract"
                    data-testid="booking-contract-checkbox"
                    checked={formData.request_contract}
                    onCheckedChange={(checked) => handleChange('request_contract', checked === true)}
                    className="mt-1 border-[#00F0FF]/60 data-[state=checked]:bg-[#00F0FF] data-[state=checked]:text-[#020408]"
                  />
                  <div className="space-y-1">
                    <Label
                      htmlFor="request_contract"
                      className="text-white flex items-center gap-2 cursor-pointer font-medium"
                    >
                      <FileSignature className="w-4 h-4 text-[#00F0FF]" />
                      Request a Service Contract
                    </Label>
                    <p className="text-sm text-gray-400 leading-relaxed">
                      Interested in an ongoing agreement with prepaid monthly discounts? Check this
                      and we'll reach out with contract options. Please note a deposit is still
                      required for your first initial appointment.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="mt-8 p-4 rounded-lg border border-[#1E293B] bg-[#0A0F14]/40 flex items-start gap-3" data-testid="booking-deposit-notice">
              <Wallet className="w-5 h-5 text-[#00F0FF] shrink-0 mt-0.5" />
              <p className="text-sm text-gray-400">
                A deposit will be required based on your estimate to secure your appointment.
                We'll send you the details once we've reviewed your request.
              </p>
            </div>
            <div className="mt-6 flex justify-end">
              <Button
                type="submit"
                data-testid="booking-submit-button"
                disabled={isSubmitting}
                className="btn-primary rounded-none px-12 py-6 font-heading tracking-wide text-lg"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <div className="spinner w-5 h-5" />
                    SUBMITTING...
                  </span>
                ) : (
                  'SUBMIT BOOKING'
                )}
              </Button>
            </div>
          </motion.form>
        </div>
      </section>
    </main>
  );
}
